/**
 * useEntitlement / useUsage — the kit's read hooks (Plan 02 D1).
 *
 * `useEntitlement(serviceId)` answers the caller's snapshot with the 5-minute
 * cache + stale-while-error grace the server's own cache semantics mirror
 * (05 §6); `useUsage()` answers the aggregated meters. Both are display-only:
 * enforcement stays on the server (EntitlementService.check + quota counters).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { billingClient } from "../client/billingClient";
import { isUnavailable, type EntitlementEntry, type EntitlementSnapshotView, type UsageResponse } from "../types";
import { billingCacheKeys, cachedRead, invalidateBillingCache, readCached } from "./billingCache";

export interface EntitlementState {
  snapshot: EntitlementSnapshotView | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

function entryToSnapshot(entry: EntitlementEntry | undefined): EntitlementSnapshotView | null {
  if (!entry || isUnavailable(entry)) return null;
  return entry;
}

/**
 * The caller's entitlement snapshot for one service. Null (with no error)
 * while loading, and also when the service answered `unavailable` — the
 * consumer renders its neutral state either way.
 */
export function useEntitlement(serviceId: string): EntitlementState {
  const [snapshot, setSnapshot] = useState<EntitlementSnapshotView | null>(() => {
    const cached = readCached<EntitlementEntry[]>(billingCacheKeys.entitlements);
    return entryToSnapshot(cached?.value.find((entry) => entry.serviceId === serviceId));
  });
  const [loading, setLoading] = useState<boolean>(() => !readCached(billingCacheKeys.entitlements)?.fresh);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const answer = await cachedRead(billingCacheKeys.entitlements, () => billingClient.getEntitlements().then((r) => r.services));
      if (!mounted.current) return;
      setSnapshot(entryToSnapshot(answer.find((entry) => entry.serviceId === serviceId)));
      setError(null);
    } catch (err) {
      if (!mounted.current) return;
      // Stale-while-error: the last good answer keeps the surface renderable.
      const cached = readCached<EntitlementEntry[]>(billingCacheKeys.entitlements);
      if (cached?.staleOk) {
        setSnapshot(entryToSnapshot(cached.value.find((entry) => entry.serviceId === serviceId)));
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : "The entitlement read failed.");
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    invalidateBillingCache(billingCacheKeys.entitlements);
    await load();
  }, [load]);

  return { snapshot, loading, error, refresh };
}

export interface UsageState {
  usage: UsageResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** The aggregated meters for every billable service (storage / bandwidth / AI). */
export function useUsage(): UsageState {
  const [usage, setUsage] = useState<UsageResponse | null>(() => readCached<UsageResponse>(billingCacheKeys.usage)?.value ?? null);
  const [loading, setLoading] = useState<boolean>(() => !readCached(billingCacheKeys.usage)?.fresh);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const answer = await cachedRead(billingCacheKeys.usage, () => billingClient.getUsage());
      if (!mounted.current) return;
      setUsage(answer);
      setError(null);
    } catch (err) {
      if (!mounted.current) return;
      const cached = readCached<UsageResponse>(billingCacheKeys.usage);
      if (cached?.staleOk) {
        setUsage(cached.value);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : "The usage read failed.");
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    invalidateBillingCache(billingCacheKeys.usage);
    await load();
  }, [load]);

  return { usage, loading, error, refresh };
}
